import React, { useCallback } from "react";
import { Box, IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import { usePagesTranslation } from "../../../locales";
import { ACCENT, NOTICE, SURFACE, TEXT } from "../../../../dark-theme/palette";
import {
    getHintPatternForWordCells,
    hintPatternHasRevealedLetter,
    removeHyphens
} from "../../common/utils/PuzzlePlayLogic";
import HintPatternBoxes from "./HintPatternBoxes";

const CLUE_FONT_MIN = 5;
const CLUE_FONT_MAX = 22;
const CELL_FONT_MIN = 6;
const CELL_FONT_MAX = 40;

function clampInt(n, min, max) {
    const x = Math.round(Number(n));
    if (Number.isNaN(x)) return min;
    return Math.min(max, Math.max(min, x));
}

/**
 * "Fill in the cells" UI: meaning preview, clue number/direction, letter input (and hint pattern when hints on).
 * Used under the grid and embedded at the top of the AI chat panel.
 */
const CrosswordFillCellsBlock = ({
    wordInputs = {},
    localWordInputValues = {},
    hintRevealCells = [],
    foundCellKeySet,
    isAdminRole = false,
    onWordInputChange,
    onWordInputSubmit,
    adminGridClueNumberFontPx = 11,
    adminGridCellFontPx = 18,
    onAdminGridClueNumberFontPxChange,
    onAdminGridCellFontPxChange,
    /** Tighter layout when shown inside the clues / AI chat column */
    embedded = false,
    /** When false, omit the "Fill in the cells" heading (e.g. duplicate in AI chat). */
    showTitle = true,
}) => {
    const { t } = usePagesTranslation();
    const entries = Object.entries(wordInputs);

    const handleClueFontInput = useCallback(
        (e) => {
            const v = clampInt(e.target.value, CLUE_FONT_MIN, CLUE_FONT_MAX);
            onAdminGridClueNumberFontPxChange?.(v);
        },
        [onAdminGridClueNumberFontPxChange]
    );

    const handleCellFontInput = useCallback(
        (e) => {
            const v = clampInt(e.target.value, CELL_FONT_MIN, CELL_FONT_MAX);
            onAdminGridCellFontPxChange?.(v);
        },
        [onAdminGridCellFontPxChange]
    );

    if (entries.length === 0 && !isAdminRole) return null;

    const outerSx = embedded
        ? {
              width: "100%",
              minWidth: 0,
              boxSizing: "border-box",
              p: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              gap: 1,
          }
        : {
              mt: 2,
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              boxSizing: "border-box",
              p: 1.5,
              backgroundColor: SURFACE.card,
              color: TEXT.primary,
              borderRadius: 1,
              border: `1px solid ${SURFACE.border}`,
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              gap: 1.5,
          };

    return (
        <Box sx={outerSx}>
            {showTitle ? (
                <Typography
                    variant={embedded ? "subtitle1" : "h6"}
                    sx={{
                        fontWeight: 600,
                        color: "#3BA666",
                        width: "100%",
                        flexShrink: 0,
                    }}
                >
                    {t("puzzle.playCross.fill-cells")}
                </Typography>
            ) : null}
            {isAdminRole &&
            (onAdminGridClueNumberFontPxChange || onAdminGridCellFontPxChange) ? (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 1.5,
                        width: "100%",
                        px: 0.5,
                        py: 0.5,
                        boxSizing: "border-box",
                    }}
                >
                    <Typography
                        variant="caption"
                        component="p"
                        sx={{
                            m: 0,
                            flex: "1 1 160px",
                            minWidth: 0,
                            color: NOTICE.warning.text,
                            fontStyle: "italic",
                            lineHeight: 1.35,
                        }}
                    >
                        {t("puzzle.playCross.admin-fill-hint")}
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            flexWrap: "wrap",
                            gap: 1,
                            alignItems: "center",
                            flexShrink: 0,
                        }}
                    >
                        {onAdminGridClueNumberFontPxChange ? (
                            <TextField
                                label={t("puzzle.playCross.admin-font-label-clue")}
                                type="number"
                                size="small"
                                value={adminGridClueNumberFontPx}
                                onChange={handleClueFontInput}
                                slotProps={{
                                    htmlInput: {
                                        min: CLUE_FONT_MIN,
                                        max: CLUE_FONT_MAX,
                                        step: 1,
                                        "aria-label": t("puzzle.playCross.admin-font-label-clue"),
                                    },
                                }}
                                sx={{ width: 92, flexShrink: 0 }}
                            />
                        ) : null}
                        {onAdminGridCellFontPxChange ? (
                            <TextField
                                label={t("puzzle.playCross.admin-font-label-letter")}
                                type="number"
                                size="small"
                                value={adminGridCellFontPx}
                                onChange={handleCellFontInput}
                                slotProps={{
                                    htmlInput: {
                                        min: CELL_FONT_MIN,
                                        max: CELL_FONT_MAX,
                                        step: 1,
                                        "aria-label": t("puzzle.playCross.admin-font-label-letter"),
                                    },
                                }}
                                sx={{ width: 92, flexShrink: 0 }}
                            />
                        ) : null}
                    </Box>
                </Box>
            ) : null}
            <Box sx={{ display: "flex", flexDirection: "column", gap: embedded ? 1 : 1.5, width: "100%", minWidth: 0 }}>
                {entries.map(([wordKey, wordInput]) => (
                    <Box
                        key={wordKey}
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            width: "100%",
                            maxWidth: "100%",
                            minWidth: 0,
                            boxSizing: "border-box",
                        }}
                    >
                        {isAdminRole && (
                            <Box
                                sx={{
                                    px: 0.5,
                                    py: 0.75,
                                    width: "100%",
                                    maxWidth: { xs: "100%", sm: 420 },
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    component="p"
                                    sx={{
                                        m: 0,
                                        fontFamily: "ui-monospace, monospace",
                                        fontWeight: 700,
                                        color: "primary.main",
                                        letterSpacing: "0.04em",
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {t("puzzle.playCross.admin-answer-label", "Answer")}: {wordInput.expectedWord}
                                </Typography>
                            </Box>
                        )}
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                                p: 1,
                                backgroundColor: SURFACE.sunken,
                                borderRadius: 1,
                                border: `1px solid ${SURFACE.border}`,
                                width: "100%",
                                maxWidth: "100%",
                                minWidth: 0,
                                boxSizing: "border-box",
                            }}
                        >
                            {wordInput.meaningPreview ? (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: "text.secondary",
                                        width: "100%",
                                        lineHeight: 1.45,
                                        fontStyle: "italic",
                                    }}
                                >
                                    {wordInput.meaningPreview}
                                </Typography>
                            ) : null}
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: { xs: "column", sm: "row" },
                                    alignItems: { xs: "stretch", sm: "center" },
                                    gap: { xs: 1.5, sm: 1 },
                                    width: "100%",
                                    minWidth: 0,
                                }}
                            >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                                    {wordInput.number && (
                                        <Box
                                            sx={{
                                                width: "21px",
                                                height: "21px",
                                                minWidth: "21px",
                                                minHeight: "21px",
                                                flexShrink: 0,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                backgroundColor: ACCENT.soft,
                                                borderRadius: "50%",
                                                border: `1px solid ${ACCENT.main}`,
                                                fontSize: "0.75rem",
                                                fontWeight: 600,
                                                color: TEXT.primary,
                                                boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                                            }}
                                        >
                                            {wordInput.number}
                                        </Box>
                                    )}
                                    <Typography
                                        variant="body2"
                                        component="span"
                                        sx={{
                                            fontWeight: 500,
                                            color: "text.secondary",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {wordInput.direction === "horizontal"
                                            ? t("puzzle.playCross.across")
                                            : t("puzzle.playCross.down")}
                                        :
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 0.5,
                                        flex: 1,
                                        minWidth: 0,
                                        width: "100%",
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder={t("puzzle.playCross.enter-word", {
                                                count: removeHyphens(wordInput.expectedWord).length,
                                            })}
                                            value={localWordInputValues[wordKey] ?? wordInput.inputValue ?? ""}
                                            onChange={(e) => onWordInputChange(wordKey, e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    onWordInputSubmit(wordKey);
                                                }
                                            }}
                                            slotProps={{
                                                htmlInput: {
                                                    maxLength: removeHyphens(wordInput.expectedWord).length,
                                                    style: {
                                                        textAlign: "center",
                                                        fontSize: embedded ? "1rem" : "1.1rem",
                                                        fontWeight: 600,
                                                    },
                                                },
                                                input: {
                                                    endAdornment: (
                                                        <InputAdornment position="end" sx={{ mr: 0.5 }}>
                                                            <Box
                                                                component="span"
                                                                sx={{
                                                                    display: "inline-flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    width: 22,
                                                                    height: 22,
                                                                    borderRadius: "50%",
                                                                    backgroundColor: ACCENT.soft,
                                                                    border: `1px solid ${ACCENT.main}`,
                                                                    fontSize: "0.75rem",
                                                                    fontWeight: 600,
                                                                    color: ACCENT.light,
                                                                }}
                                                            >
                                                                {(localWordInputValues[wordKey] ?? wordInput.inputValue ?? "")
                                                                    .length}
                                                            </Box>
                                                        </InputAdornment>
                                                    ),
                                                },
                                            }}
                                            sx={{
                                                flex: 1,
                                                minWidth: 0,
                                                "& .MuiOutlinedInput-root": {
                                                    backgroundColor: SURFACE.sunken,
                                                    color: TEXT.primary,
                                                    "&:hover": {
                                                        backgroundColor: SURFACE.card,
                                                    },
                                                },
                                                "& .MuiInputBase-input::placeholder": {
                                                    fontSize: "0.875rem",
                                                    fontWeight: 400,
                                                    opacity: 0.6,
                                                    textTransform: "none",
                                                },
                                            }}
                                        />
                                        <IconButton
                                            onClick={() => onWordInputSubmit(wordKey)}
                                            disabled={
                                                !(localWordInputValues[wordKey] ?? wordInput.inputValue) ||
                                                removeHyphens(
                                                    (localWordInputValues[wordKey] ?? wordInput.inputValue) || ""
                                                ).length !== removeHyphens(wordInput.expectedWord).length
                                            }
                                            size="small"
                                            sx={{
                                                backgroundColor: ACCENT.main,
                                                color: ACCENT.contrastText,
                                                width: "32px",
                                                height: "32px",
                                                minWidth: "32px",
                                                minHeight: "32px",
                                                borderRadius: "50%",
                                                "&:hover": {
                                                    backgroundColor: ACCENT.dark,
                                                },
                                                "&.Mui-disabled": {
                                                    backgroundColor: SURFACE.hover,
                                                    color: TEXT.disabled,
                                                },
                                            }}
                                        >
                                            ✓
                                        </IconButton>
                                    </Box>
                                    {(() => {
                                        const rowPattern = getHintPatternForWordCells(
                                            wordInput.expectedWord,
                                            wordInput.cells,
                                            hintRevealCells,
                                            foundCellKeySet
                                        );
                                        return rowPattern && hintPatternHasRevealedLetter(rowPattern) ? (
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: { xs: "center", sm: "flex-start" },
                                                    width: "100%",
                                                    mt: 0.25,
                                                }}
                                            >
                                                <HintPatternBoxes pattern={rowPattern} boxSize={embedded ? 20 : 22} />
                                            </Box>
                                        ) : null;
                                    })()}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

export default CrosswordFillCellsBlock;
